// File: frontend/src/components/Navbar/NotificationDrawer.js

import React, { useEffect, useState } from "react";
import {
  Box,
  Badge,
  IconButton,
  Tooltip,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Stack,
  Text,
  Button,
  Input,
  useToast,
  Textarea,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  useDisclosure,
  Spinner,
  HStack,
  Link,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/icons";
import { FaBell, FaShoppingCart, FaMoneyBillWave, FaWallet, FaInfoCircle, FaUsers, FaShieldAlt, FaCrown, FaMusic, FaBriefcase } from "react-icons/fa";
import Select from "react-select";
import moment from "moment";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

// GraphQL operations
const GET_NOTIFICATIONS = gql`
  query Notifications($to: ID!, $limit: Int, $offset: Int, $category: NotificationCategoryType, $unreadOnly: Boolean) {
    notifications(to: $to, limit: $limit, offset: $offset, category: $category, unreadOnly: $unreadOnly) {
      message_id: id
      sender_id: senderId
      sender_name: senderName
      sender_username: senderUsername
      is_system_generated: isSystemGenerated
      category
      priority
      message { title content }
      action_url: actionUrl
      timestamp
      read_by: readBy
      recipient_ids: recipientIds
    }
  }
`;

const GET_SENT = gql`
  query SentNotifications($from_: ID!) {
    sentNotifications(from_: $from_) {
      message_id: id
      sender_id: senderId
      sender_name: senderName
      sender_username: senderUsername
      is_system_generated: isSystemGenerated
      category
      priority
      recipient_ids: recipientIds
      message { title content }
      action_url: actionUrl
      timestamp
    }
  }
`;

const ON_NOTIFICATION = gql`
  subscription OnNotification($to: ID!) {
    onNotification(to: $to) {
      message_id: id
      sender_id: senderId
      sender_name: senderName
      sender_username: senderUsername
      is_system_generated: isSystemGenerated
      category
      priority
      message { title content }
      action_url: actionUrl
      timestamp
      read_by: readBy
      recipient_ids: recipientIds
    }
  }
`;

const GET_UNREAD_COUNT = gql`
  query UnreadNotificationCount($userId: ID!) {
    unreadNotificationCount(userId: $userId)
  }
`;

const CREATE_NOTIFICATION = gql`
  mutation CreateNotification(
    $to: [ID!]!,
    $message: MessageInput!,
    $senderId: ID!,
    $senderName: String!,
    $senderUsername: String!
  ) {
    createNotification(
      to: $to,
      message: $message,
      senderId: $senderId,
      senderName: $senderName,
      senderUsername: $senderUsername
    ) {
      id
    }
  }
`;


const MARK_ALL_READ = gql`
  mutation MarkAllNotificationsAsRead($userId: ID!) {
    markAllNotificationsAsRead(userId: $userId)
  }
`;

// Helper functions for notification categories and priorities
const getCategoryColor = (category) => {
  const colors = {
    PURCHASE: "green",
    EARNING: "purple",
    PAYMENT: "blue",
    WALLET: "cyan",
    SYSTEM: "gray",
    SOCIAL: "pink",
    SECURITY: "red",
    SUBSCRIPTION: "orange",
    ARTIST_UPDATE: "teal",
    PORTFOLIO: "yellow",
  };
  return colors[category] || "gray";
};

const getCategoryIcon = (category) => {
  const icons = {
    PURCHASE: FaShoppingCart,
    EARNING: FaMoneyBillWave,
    PAYMENT: FaMoneyBillWave,
    WALLET: FaWallet,
    SYSTEM: FaInfoCircle,
    SOCIAL: FaUsers,
    SECURITY: FaShieldAlt,
    SUBSCRIPTION: FaCrown,
    ARTIST_UPDATE: FaMusic,
    PORTFOLIO: FaBriefcase,
  };
  return icons[category] || FaInfoCircle;
};

const getPriorityColor = (priority) => {
  const colors = {
    LOW: "gray",
    NORMAL: "blue",
    HIGH: "orange",
    CRITICAL: "red",
  };
  return colors[priority] || "gray";
};

export default function NotificationDrawer() {
  const { user, isAuthenticated } = useAuth();
  const drawer = useDisclosure();
  const popover = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  // 1) Query with subscribeToMore - only run if user is authenticated
  const {
    data: allData,
    loading: loadingNotifications,
    refetch: refetchAll,
    subscribeToMore,
  } = useQuery(
    GET_NOTIFICATIONS,
    {
      variables: { to: user?.user_id },
      skip: !isAuthenticated || !user?.user_id,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        console.error('Failed to load notifications:', error);
      }
    }
  );

useEffect(() => {
  if (!isAuthenticated || !user?.user_id || !subscribeToMore) {
    return;
  }

  try {
    const unsubscribe = subscribeToMore({
      document: ON_NOTIFICATION,
      variables: { to: user.user_id },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData?.data) return prev;

        const newNotif = subscriptionData.data.onNotification;

        // In-app toast notification
        const senderInfo = newNotif.is_system_generated
          ? 'System Notification'
          : (newNotif.sender_name || 'Someone');

        toast({
          title: newNotif.message?.title || 'New Notification',
          description: newNotif.is_system_generated
            ? newNotif.message?.content
            : `From ${senderInfo}`,
          status: newNotif.priority === 'CRITICAL' || newNotif.priority === 'HIGH' ? "warning" : "info",
          duration: 5000,
          isClosable: true,
          position: "top-right"
        });

        // Browser push notification
        if ("Notification" in window && newNotif.message) {
          if (Notification.permission === "granted") {
            new Notification(newNotif.message.title, {
              body: newNotif.message.content,
              icon: '/logo192.png',
              tag: newNotif.message_id
            });
          } else if (Notification.permission !== "denied") {
            Notification.requestPermission().then(perm => {
              if (perm === "granted") {
                new Notification(newNotif.message.title, {
                  body: newNotif.message.content,
                  icon: '/logo192.png',
                  tag: newNotif.message_id
                });
              }
            });
          }
        }

        // Merge into list
        return {
          ...prev,
          notifications: [newNotif, ...(prev.notifications || [])],
        };
      },
      onError: (err) => {
        console.error('Notification subscription error:', err);
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  } catch (error) {
    console.error('Failed to setup notification subscription:', error);
  }
}, [subscribeToMore, user?.user_id, toast, isAuthenticated]);

  // 3) Sent notifications query
  const { data: sentData, loading: loadingSent, refetch: refetchSent } = useQuery(
    GET_SENT,
    { 
      variables: { from_: user?.user_id }, 
      skip: !isAuthenticated || !user?.user_id || user.account_type === "fan" 
    }
  );

  // 4) Send notification mutation
  const [createNotification, { loading: sendingNotification }] = useMutation(
    CREATE_NOTIFICATION,
    {
      onCompleted: () => {
        toast({ title: "Notification sent", status: "success" });
        // Clear form data after successful submission
        setNewNotification({ title: "", content: "" });
        setSelectedRecipients([]);
        popover.onClose();
        refetchAll();
        if (user.account_type !== "fan") refetchSent();
      },
      onError: err =>
        toast({ title: "Send failed", description: err.message, status: "error" }),
    }
  );


  const [markAllRead] = useMutation(
    MARK_ALL_READ, 
    {
      variables: { userId: user.user_id },
      onCompleted: () => refetchAll(),
      onError: (err) => console.error("Error marking notifications as read:", err)
    }
  );

  const [newNotification, setNewNotification] = useState({ title: "", content: "" });
  const [options, setOptions] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  useEffect(() => {
    const followers = user.account.profile?.followers || [];
    const followerOptions = followers.map(f => ({ 
      value: f.follower_id, 
      label: f.follower_name 
    }));
    
    // Add "Select All" option only if there are followers
    if (followers.length > 0) {
      setOptions([
        { 
          value: 'all', 
          label: '✓ Select All Recipients' 
        },
        ...followerOptions
      ]);
    } else {
      setOptions(followerOptions);
    }
  }, [user]);

  const unreadCount =
    allData?.notifications?.filter(n => !n.read_by?.includes(user?.user_id)).length || 0;

  const handleSendNotification = () => {
    // Get actual recipient IDs from selected options
    const toIds = selectedRecipients
      .filter(option => option.value !== 'all') // Filter out "Select All" option
      .map(option => option.value);
    
    if (toIds.length === 0) {
      toast({
        title: "No recipients selected",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    
    createNotification({
      variables: {
        to: toIds,
        message: newNotification,
        senderId: user.user_id,
        senderName: `${user.firstname} ${user.lastname}`,
        senderUsername: user.username,
      },
    });
  };

  // Create a handler function for the Select component
  const handleSelectChange = (selectedOptions) => {
    if (!selectedOptions) {
      setSelectedRecipients([]);
      return;
    }

    // Check if "Select All" was just selected
    if (selectedOptions.some(option => option.value === 'all') && 
        !selectedRecipients.some(option => option.value === 'all')) {
      // Select all available options
      setSelectedRecipients(options);
      return;
    }
    
    // Check if "Select All" was just deselected
    if (!selectedOptions.some(option => option.value === 'all') && 
        selectedRecipients.some(option => option.value === 'all')) {
      // Deselect all options
      setSelectedRecipients([]);
      return;
    }
    
    // Handle normal selection
    setSelectedRecipients(selectedOptions);
  };

  const handleDrawerClose = () => {
    if (unreadCount > 0) {
      markAllRead();
    }
    drawer.onClose();
  };

  return (
    <>
      {/* Notification Icon */}
      <Box position="relative" mx={2}>
        {unreadCount > 0 && (
          <Badge 
            position="absolute" 
            bg="dark.900" 
            color="primary.100" 
            left={7} 
            top={-2}
            borderRadius="full"
            fontSize="0.8em"
            p="1px 6px"
          >
            {unreadCount}
          </Badge>
        )}
        <Tooltip label="Notifications" fontSize="md">
          <IconButton
            icon={<Icon as={FaBell} />}
            aria-label="Open Notifications"
            variant="ghost"
            color="dark.900" 
            _hover={{ bg: "secondary.100", color: "dark.900" }}
            onClick={drawer.onOpen}
          />
        </Tooltip>
      </Box>

      {/* Notification Drawer */}
      <Drawer isOpen={drawer.isOpen} onClose={handleDrawerClose} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent bg="primary.100" color="dark.100">
          <DrawerCloseButton color="dark.100" />
          <DrawerHeader 
            textAlign="center" 
          
          
            color="dark.500"
            bg="primary.500"
          >
            Notifications
          </DrawerHeader>

          <DrawerBody>
            <Tabs variant="enclosed-colored" align="center" >
              <TabList mb={4}>
                <Tab 
              
                >
                  All Notifications
                </Tab>
                <Tab 
                  display={user.account_type === "fan" ? "none" : "block"}
            
                >
                  Sent Notifications
                </Tab>
              </TabList>

              <TabPanels>
                {/* All Notifications */}
                <TabPanel>
                  {loadingNotifications ? (
                    <Spinner />
                  ) : allData?.notifications?.length > 0 ? (
                    <Accordion allowToggle>
                      {allData?.notifications?.map(notification => (
                        <AccordionItem
                          key={notification.message_id}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={!notification.read_by.includes(user.user_id) ? "dark.100" : "secondary.900"}
                          bg={!notification.read_by.includes(user.user_id) ? "primary.500" : "primary.100"}
                          my={2}
                        >
                          <AccordionButton _hover={{ bg: "primary.500" }}>
                            <Box flex="1" textAlign="left">
                              <HStack spacing={2} mb={1}>
                                <Icon
                                  as={getCategoryIcon(notification.category)}
                                  color={`${getCategoryColor(notification.category)}.500`}
                                />
                                <Text fontWeight="bold" color="dark.900">{notification.message.title}</Text>
                              </HStack>
                              <HStack spacing={2} flexWrap="wrap">
                                <Badge
                                  colorScheme={getCategoryColor(notification.category)}
                                  fontSize="xs"
                                >
                                  {notification.category?.replace('_', ' ')}
                                </Badge>
                                {notification.priority && notification.priority !== 'NORMAL' && (
                                  <Badge
                                    colorScheme={getPriorityColor(notification.priority)}
                                    fontSize="xs"
                                  >
                                    {notification.priority}
                                  </Badge>
                                )}
                                {!notification.read_by.includes(user.user_id) && (
                                  <Badge colorScheme="teal" bg="dark.900" color="primary.100">Unread</Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="secondary.500" mt={1}>
                                {notification.is_system_generated ? "System" : notification.sender_name} •{" "}
                                {moment(notification.timestamp).fromNow()}
                              </Text>
                            </Box>
                            <AccordionIcon color="dark.900" />
                          </AccordionButton>
                          <AccordionPanel pb={4} bg="primary.900" color="dark.100">
                            <Text mb={3}>{notification.message.content}</Text>
                            {notification.action_url && (
                              <Link
                                as={Button}
                                size="sm"
                                variant="solid"
                                colorScheme={getCategoryColor(notification.category)}
                                onClick={() => {
                                  navigate(notification.action_url);
                                  handleDrawerClose();
                                }}
                              >
                                View Details
                              </Link>
                            )}
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <Text>No notifications available</Text>
                  )}
                </TabPanel>

                {/* Sent Notifications */}
                <TabPanel display={user.account_type === "fan" ? "none" : "block"}>
                  {loadingSent ? (
                    <Spinner />
                  ) : sentData?.sentNotifications?.length > 0 ? (
                    <Accordion allowToggle>
                      {sentData.sentNotifications.map(msg => (
                        <AccordionItem
                          key={msg.message_id}
                          borderRadius="md"
                          my={2}
                          border="1px solid"
                          borderColor="secondary.900"
                          bg="primary.100"
                        >
                          <AccordionButton _hover={{ bg: "primary.500" }}>
                            <Box flex="1" textAlign="left">
                              <HStack spacing={2} mb={1}>
                                <Icon
                                  as={getCategoryIcon(msg.category)}
                                  color={`${getCategoryColor(msg.category)}.500`}
                                />
                                <Text fontWeight="bold" color="dark.900">{msg.message.title}</Text>
                              </HStack>
                              <HStack spacing={2} flexWrap="wrap">
                                <Badge
                                  colorScheme={getCategoryColor(msg.category)}
                                  fontSize="xs"
                                >
                                  {msg.category?.replace('_', ' ')}
                                </Badge>
                                {msg.priority && msg.priority !== 'NORMAL' && (
                                  <Badge
                                    colorScheme={getPriorityColor(msg.priority)}
                                    fontSize="xs"
                                  >
                                    {msg.priority}
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="secondary.500" mt={1}>
                                Sent to {msg.recipient_ids.length} recipient{msg.recipient_ids.length > 1 ? 's' : ''} •{" "}
                                {moment(msg.timestamp).fromNow()}
                              </Text>
                            </Box>
                            <AccordionIcon color="dark.900" />
                          </AccordionButton>
                          <AccordionPanel pb={4} color="dark.100" bg="primary.900">
                            <Text>{msg.message.content}</Text>
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <Text>No sent messages available</Text>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>

          {/* Send Notification Popover */}
          <DrawerFooter display={user.account_type === "fan" ? "none" : "flex"}>  
            <Popover isOpen={popover.isOpen} onClose={popover.onClose}>
              <PopoverTrigger>
                <Button 
                  w="100%" 
                  onClick={popover.onOpen}
                  variant="brand"
                >
                  Send New Notification
                </Button>
              </PopoverTrigger>
              <PopoverContent bg="primary.100" borderColor="dark.100">
                <PopoverArrow bg="dark.900" />
                <PopoverBody>
                  <Stack spacing={4} p={4}>
                    <Input
                      variant="light"
                      placeholder="Title"
                      value={newNotification.title}
                      onChange={e => setNewNotification(d => ({ ...d, title: e.target.value }))}
                      color="dark.100"
                      _placeholder={{ color: "primary.500" }}
                    />
                    <Textarea
                      variant="light"
                      placeholder="Content"
                      value={newNotification.content}
                      onChange={e => setNewNotification(d => ({ ...d, content: e.target.value }))}
                      color="dark.100"
                      _placeholder={{ color: "primary.500" }}
                    />
                    <Select
                      options={options}
                      isMulti 
                      placeholder="Select recipients" 
                      value={selectedRecipients}
                      onChange={handleSelectChange}
                      closeMenuOnSelect={false}
                      
                    />
                    <Button 
                      isLoading={sendingNotification} 
                      onClick={handleSendNotification}
                      variant="brand"
                    >
                      Send Notification
                    </Button>
                  </Stack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}